/**
 * Date formatting utilities for displaying purchase dates with client clock time
 * Meets acceptance criteria:
 * - Purchase time matches device local time for the same instant
 * - Format adjusts automatically based on device locale and time preference (12h/24h)
 * - Correct handling during DST changes and when user travels between time zones
 * - Fallback format: DDD MMM, YYYY - HH:MM if locale cannot be detected
 */

export interface FormattedDateTime {
  date: string;
  time: string;
  fullDateTime: string;
}

export interface DateFormatOptions {
  locale?: string;
  use24Hour?: boolean;
  includeSeconds?: boolean;
  fallbackFormat?: boolean;
}

/**
 * Detects user's locale and time preferences from browser
 * Respects the app's current language setting from i18next
 */
export const detectUserPreferences = (): { locale: string; use24Hour: boolean } => {
  try {
    // Get app's current language from i18next if available
    let appLocale = 'es'; // Default to Spanish
    
    // Try to get current language from i18next
    if (typeof window !== 'undefined' && (window as any).i18n) {
      appLocale = (window as any).i18n.language || 'es';
    }
    
    // Map app language codes to locale codes
    const localeMap: { [key: string]: string } = {
      'es': 'es-ES',
      'en': 'en-US', 
      'fr': 'fr-FR',
      'pt': 'pt-BR'
    };
    
    const browserLocale = localeMap[appLocale] || 'es-ES';
    
    // Detect 12h/24h preference from browser
    const testDate = new Date();
    const timeString = testDate.toLocaleTimeString(browserLocale, { 
      hour: 'numeric',
      hour12: undefined // Let browser decide
    });
    
    // Check if time contains AM/PM indicators
    const use24Hour = !timeString.includes('AM') && !timeString.includes('PM');
    
    return {
      locale: browserLocale,
      use24Hour
    };
  } catch (error) {
    console.warn('Could not detect user preferences, using fallbacks:', error);
    return {
      locale: 'es-ES', // Default to Spanish
      use24Hour: true
    };
  }
};

/**
 * Parses a server timestamp (orders, logs, notifications...). The backend
 * sends naive UTC like "2026-10-12T10:28:49.650290" (no "Z"), which
 * `new Date()` would read as local time, hours off from the real moment.
 * Not for event start/end dates: those are local wall-clock times.
 */
export const parseServerDate = (dateString: string): Date => {
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateString);
  return new Date(hasTimezone ? dateString : `${dateString}Z`);
};

/**
 * Gets the user's current timezone, handling DST and travel scenarios
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Could not detect timezone, using UTC:', error);
    return 'UTC';
  }
};

/**
 * Formats a date string to show both date and time using the client's local timezone
 * Automatically detects user preferences and handles DST/timezone changes
 * @param dateString - ISO date string from the server
 * @param options - Formatting options
 * @returns Object containing formatted date, time, and full datetime
 */
export const formatPurchaseDateTime = (
  dateString: string, 
  options: DateFormatOptions = {}
): FormattedDateTime => {
  try {
    let date: Date;
    
    // Server sends dates without timezone info, so we need to explicitly treat them as UTC
    // Check if the date string has timezone information
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      // Date already has timezone info, parse as-is
      date = new Date(dateString);
    } else {
      // No timezone info - assume it's UTC and add 'Z'
      date = new Date(dateString + 'Z');
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return {
        date: 'Invalid Date',
        time: 'Invalid Time',
        fullDateTime: 'Invalid Date and Time'
      };
    }

    // Detect user preferences if not provided
    const userPrefs = options.locale ? 
      { locale: options.locale, use24Hour: options.use24Hour ?? true } : 
      detectUserPreferences();
    
    const timezone = getUserTimezone();
    const use24Hour = options.use24Hour ?? userPrefs.use24Hour;
    const includeSeconds = options.includeSeconds ?? true;

    // Fallback format if locale detection fails
    if (options.fallbackFormat) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = dayNames[date.getDay()];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      const fallbackDate = `${day} ${month}, ${year}`;
      const fallbackTime = `${hours}:${minutes}`;
      const fallbackDateTime = `${fallbackDate} - ${fallbackTime}`;
      
      return {
        date: fallbackDate,
        time: fallbackTime,
        fullDateTime: fallbackDateTime
      };
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: timezone
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !use24Hour,
      timeZone: timezone
    };

    if (includeSeconds) {
      timeOptions.second = '2-digit';
    }

    const fullDateTimeOptions: Intl.DateTimeFormatOptions = {
      ...dateOptions,
      ...timeOptions
    };

    return {
      date: date.toLocaleDateString(userPrefs.locale, dateOptions),
      time: date.toLocaleTimeString(userPrefs.locale, timeOptions),
      fullDateTime: date.toLocaleString(userPrefs.locale, fullDateTimeOptions)
    };
  } catch (error) {
    console.error('Error formatting date:', error, 'Date string:', dateString);
    return {
      date: 'Date Error',
      time: 'Time Error',
      fullDateTime: 'Date and Time Error'
    };
  }
};

/**
 * Formats a date string for display in order cards and lists
 * Shows date and time in a compact format using client's local timezone
 * Automatically detects user preferences and handles DST/timezone changes
 * @param dateString - ISO date string from the server
 * @param options - Formatting options
 * @returns Formatted date and time string
 */
export const formatOrderDate = (
  dateString: string, 
  options: DateFormatOptions = {}
): string => {
  try {
    let date: Date;
    
    // Server sends dates without timezone info, so we need to explicitly treat them as UTC
    // Check if the date string has timezone information
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      // Date already has timezone info, parse as-is
      date = new Date(dateString);
    } else {
      // No timezone info - assume it's UTC and add 'Z'
      date = new Date(dateString + 'Z');
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }

    // Detect user preferences if not provided
    const userPrefs = options.locale ? 
      { locale: options.locale, use24Hour: options.use24Hour ?? true } : 
      detectUserPreferences();
    
    const timezone = getUserTimezone();
    const use24Hour = options.use24Hour ?? userPrefs.use24Hour;
    const includeSeconds = options.includeSeconds ?? true;

    // Fallback format if locale detection fails
    if (options.fallbackFormat) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = dayNames[date.getDay()];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day} ${month}, ${year} - ${hours}:${minutes}`;
    }

    const formatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: !use24Hour,
      timeZone: timezone
    };

    if (includeSeconds) {
      formatOptions.second = '2-digit';
    }

    return date.toLocaleString(userPrefs.locale, formatOptions);
  } catch (error) {
    console.error('Error formatting date:', error, 'Date string:', dateString);
    return 'Date Error';
  }
};

/**
 * Formats a date string for display in detailed views (like order confirmation)
 * Shows full date and time with seconds using client's local timezone
 * Automatically detects user preferences and handles DST/timezone changes
 * @param dateString - ISO date string from the server
 * @param options - Formatting options
 * @returns Formatted date and time string with full details
 */
export const formatDetailedOrderDate = (
  dateString: string, 
  options: DateFormatOptions = {}
): string => {
  try {
    let date: Date;
    
    // Server sends dates without timezone info, so we need to explicitly treat them as UTC
    // Check if the date string has timezone information
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      // Date already has timezone info, parse as-is
      date = new Date(dateString);
    } else {
      // No timezone info - assume it's UTC and add 'Z'
      date = new Date(dateString + 'Z');
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date and Time';
    }

    // Detect user preferences if not provided
    const userPrefs = options.locale ? 
      { locale: options.locale, use24Hour: options.use24Hour ?? true } : 
      detectUserPreferences();
    
    const timezone = getUserTimezone();
    const use24Hour = options.use24Hour ?? userPrefs.use24Hour;
    const includeSeconds = options.includeSeconds ?? true;

    // Fallback format if locale detection fails
    if (options.fallbackFormat) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = dayNames[date.getDay()];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${day} ${month}, ${year} - ${hours}:${minutes}:${seconds}`;
    }

    const formatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: !use24Hour,
      timeZone: timezone
    };

    if (includeSeconds) {
      formatOptions.second = '2-digit';
    }

    return date.toLocaleString(userPrefs.locale, formatOptions);
  } catch (error) {
    console.error('Error formatting date:', error, 'Date string:', dateString);
    return 'Date and Time Error';
  }
};

/**
 * Gets the current client time in a formatted string
 * Automatically detects user preferences
 * @param options - Formatting options
 * @returns Current client time formatted string
 */
export const getCurrentClientTime = (options: DateFormatOptions = {}): string => {
  const now = new Date();
  const userPrefs = options.locale ? 
    { locale: options.locale, use24Hour: options.use24Hour ?? true } : 
    detectUserPreferences();
  
  const timezone = getUserTimezone();
  const use24Hour = options.use24Hour ?? userPrefs.use24Hour;
  const includeSeconds = options.includeSeconds ?? true;

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24Hour,
    timeZone: timezone
  };

  if (includeSeconds) {
    timeOptions.second = '2-digit';
  }

  return now.toLocaleTimeString(userPrefs.locale, timeOptions);
};

/**
 * Gets the current client date and time in a formatted string
 * Automatically detects user preferences
 * @param options - Formatting options
 * @returns Current client date and time formatted string
 */
export const getCurrentClientDateTime = (options: DateFormatOptions = {}): string => {
  const now = new Date();
  const userPrefs = options.locale ? 
    { locale: options.locale, use24Hour: options.use24Hour ?? true } : 
    detectUserPreferences();
  
  const timezone = getUserTimezone();
  const use24Hour = options.use24Hour ?? userPrefs.use24Hour;
  const includeSeconds = options.includeSeconds ?? true;

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24Hour,
    timeZone: timezone
  };

  if (includeSeconds) {
    formatOptions.second = '2-digit';
  }

  return now.toLocaleString(userPrefs.locale, formatOptions);
};

/**
 * Validates if a date string represents the same instant as the current client time
 * Useful for testing timezone conversion accuracy
 * @param dateString - ISO date string from the server
 * @returns Object with validation results
 */
export const validateTimezoneConversion = (dateString: string) => {
  try {
    let serverDate: Date;
    
    // Parse server date as UTC
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      serverDate = new Date(dateString);
    } else {
      serverDate = new Date(dateString + 'Z');
    }
    
    const clientNow = new Date();
    const timeDiff = Math.abs(clientNow.getTime() - serverDate.getTime());
    const isRecent = timeDiff < 24 * 60 * 60 * 1000; // Within 24 hours
    
    return {
      isValid: !isNaN(serverDate.getTime()),
      isRecent,
      timeDifferenceMs: timeDiff,
      serverTime: serverDate.toISOString(),
      clientTime: clientNow.toISOString(),
      timezone: getUserTimezone()
    };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Debug function to help understand date conversion issues
 * Shows both server time and client time for comparison
 * @param dateString - ISO date string from the server
 * @returns Object with debug information
 */
export const debugDateConversion = (dateString: string) => {
  const originalDate = new Date(dateString);
  const clientTimezone = getUserTimezone();
  const clientOffset = new Date().getTimezoneOffset();
  
  return {
    originalString: dateString,
    parsedDate: originalDate.toISOString(),
    clientTimezone,
    clientOffsetMinutes: clientOffset,
    clientTime: originalDate.toLocaleString('es-ES', {
      timeZone: clientTimezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }),
    utcTime: originalDate.toISOString().substring(11, 19)
  };
};
